<script setup lang="ts">
import { Trash2, Upload } from 'lucide-vue-next';
import { onBeforeUnmount, ref } from 'vue';
import { toast } from 'vue3-toastify';

import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import { parseIncentiveApiError } from '../utils';

const props = withDefaults(defineProps<{
  disabled?: boolean;
  modelValue: null | string;
}>(), {
  disabled: false,
});

const emit = defineEmits<{
  cleared: [];
  'update:modelValue': [value: null | string];
}>();

const isUploading = ref(false);
const uploadProgress = ref(0);
const fileInput = ref<HTMLInputElement | null>(null);
const uploadError = ref<null | string>(null);

/**
 * The actual upload only happens when the parent form calls `upload()` (typically on
 * save), not on file selection — so a picked file sits here, unsent, until then.
 * `previewUrl` (via `URL.createObjectURL`) is the only way to show it, since the upload
 * endpoint returns an opaque id, never a URL — no preview exists for a banner that was
 * already uploaded in an earlier session (e.g. reopening the edit form), only for one
 * picked in this one.
 */
const selectedFile = ref<File | null>(null);
const previewUrl = ref<null | string>(null);

function clearBanner() {
  uploadError.value = null;
  uploadProgress.value = 0;
  selectedFile.value = null;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = null;
  emit('update:modelValue', null);
  emit('cleared');
}

function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  target.value = '';

  if (!file) return;

  uploadError.value = null;
  selectedFile.value = file;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = URL.createObjectURL(file);
}

function openFileDialog() {
  if (props.disabled || isUploading.value) return;
  fileInput.value?.click();
}

/**
 * Called by the parent form right before it submits. Uploads the pending file (if any)
 * and returns the id to send in the create/update payload — or the existing
 * `modelValue` unchanged when nothing new was picked, so callers can always trust the
 * return value as the final banner id. Throws (and surfaces `uploadError`) on failure,
 * so the caller can abort the save instead of submitting a stale/incomplete banner.
 */
async function upload(): Promise<null | string> {
  if (!selectedFile.value) return props.modelValue;

  isUploading.value = true;
  uploadProgress.value = 0;
  uploadError.value = null;

  try {
    const bannerFileExternalId = await campaignManagementService.uploadIncentiveBanner(
      selectedFile.value,
      (percent) => { uploadProgress.value = percent; },
    );
    selectedFile.value = null;
    emit('update:modelValue', bannerFileExternalId);
    return bannerFileExternalId;
  } catch (error) {
    const { message } = parseIncentiveApiError(error);
    uploadError.value = message;
    toast.error(`Falha ao enviar o banner: ${message}`);
    throw error;
  } finally {
    isUploading.value = false;
  }
}

defineExpose({ previewUrl, upload });

onBeforeUnmount(() => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});
</script>

<template>
  <div class="banner-uploader">
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="d-none"
      aria-label="Selecionar arquivo de banner"
      :disabled="disabled || isUploading"
      @change="onFileSelected"
    />

    <div v-if="previewUrl || modelValue" class="banner-preview-row">
      <img v-if="previewUrl" :src="previewUrl" alt="Pré-visualização do banner" class="banner-preview" />

      <div class="banner-current">
        <span class="banner-current-label">
          <template v-if="isUploading">Enviando… {{ uploadProgress }}%</template>
          <template v-else-if="selectedFile">Selecionado — será enviado ao salvar.</template>
          <template v-else-if="modelValue">Banner enviado ({{ modelValue }})</template>
        </span>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="disabled || isUploading"
          @click="clearBanner"
        >
          <Trash2 class="icon icon-sm me-1" />
          Limpar
        </button>
      </div>

      <div
        v-if="isUploading"
        class="progress banner-progress"
        role="progressbar"
        :aria-valuenow="uploadProgress"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class="progress-bar" :style="{ width: `${uploadProgress}%` }" />
      </div>
    </div>

    <button
      type="button"
      class="btn btn-outline-secondary btn-sm"
      :disabled="disabled || isUploading"
      @click="openFileDialog"
    >
      <Upload class="icon icon-sm me-1" />
      {{ modelValue || previewUrl ? 'Substituir banner' : 'Enviar banner' }}
    </button>

    <div v-if="uploadError" class="banner-error" role="alert">
      {{ uploadError }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner-uploader {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.banner-preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.banner-preview {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e4e6eb;
}

.banner-current {
  display: flex;
  align-items: center;
  gap: 8px;
}

.banner-current-label {
  font-size: 12px;
  color: #6b7280;
}

.banner-progress {
  flex-basis: 160px;
  height: 6px;
}

.banner-error {
  flex-basis: 100%;
  font-size: 12px;
  color: #dc3545;
}
</style>
